import React, { useEffect, useState } from 'react';

import SearchInput from 'common/inputs/SearchInput';
import { type Option } from 'common/ui/common/select/SelectCommon';
import SingleSelect from 'common/ui/SingleSelect';
import useDelayer from 'common/util/useDelayer';

import 'css/components/subdomain/admin/AdminRoadmap/_TextFilter.scss';

import {
  alphaNumericOptions,
  numericOptions,
  textFilterOptionsSettings,
} from './FilterConstants/TextFilterConstants';
import useCustomQueryParam from './useCustomQueryParam';

type inputTypes = 'number' | 'text';

type Props = {
  fieldID: string;
  inputType: inputTypes;
  isCustomDescriptionColumn: boolean;
};

interface TextOption {
  operator?: Option;
  value?: string;
}

const determineOptions = (inputType: inputTypes) => {
  switch (inputType) {
    case 'number':
      return numericOptions;
    case 'text':
      return alphaNumericOptions;
    default:
      return alphaNumericOptions;
  }
};

const TextFilter = ({ fieldID, inputType, isCustomDescriptionColumn }: Props) => {
  const [textOption, setTextOption] = useState<TextOption>({
    operator: undefined,
    value: undefined,
  });
  const [loading, setLoading] = useState(true);

  const [options] = useState(determineOptions(inputType));

  const { getCustomQueryParam, setCustomQueryParams } = useCustomQueryParam(
    isCustomDescriptionColumn ? 'description' : 'factor'
  );

  const onTextChangeAfterDelay = (fieldID: string, textOption: Option, value: string) => {
    setCustomQueryParams(fieldID, textOption.value, [value]);
  };

  const searchDelayer = useDelayer(onTextChangeAfterDelay, 500);

  useEffect(() => {
    const selectedOption = getCustomQueryParam(fieldID);
    if (!selectedOption) {
      setTextOption({
        operator: undefined,
        value: undefined,
      });
    } else {
      setTextOption({
        operator: options.find((option) => option.value === selectedOption.operator),
        value: selectedOption.value[0],
      });
    }
    setLoading(false);
  }, [fieldID, getCustomQueryParam, options]);

  const changeOption = (option?: Option) => {
    setLoading(true);
    const newKey = option && textFilterOptionsSettings[option.value] ? option.value : null;
    // Reset search value to empty every time the option changes
    setCustomQueryParams(fieldID, newKey, []);
  };

  const onTextChange = (value: string) => {
    if (value) {
      searchDelayer(fieldID, textOption.operator, value);
      return;
    }

    // If the value does not exist, clear it immediately since that won't trigger an API call
    setCustomQueryParams(fieldID, textOption?.operator?.value ?? null, [value]);
  };

  return (
    <div className="textFilter">
      <SingleSelect
        allowClear={true}
        labelPlacement="top"
        loading={loading}
        onChange={changeOption}
        options={options}
        optionsPlacement="bottom"
        value={textOption.operator}
        placeholder="All"
        withBorder={true}
      />
      {textOption.operator &&
        textFilterOptionsSettings[textOption.operator.value] &&
        textFilterOptionsSettings[textOption.operator.value].requiresAdditionalValue && (
          <SearchInput
            autoFocus={false}
            showClearIcon={false}
            showSearchIcon={false}
            defaultValue={textOption.value}
            onChange={onTextChange}
            placeholder={inputType === 'number' ? 'Value' : 'Keywords'}
            styling="v2"
            inputMode={inputType}
            type={inputType}
          />
        )}
    </div>
  );
};

export default TextFilter;
